import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface UnansweredConversation {
  id: string;
  customer_accept: boolean | null;
  confirm_booking: boolean | null;
  customer_deny: boolean | null;
  last_activity_at: string;
  customer_request_id: string;
  customer_last_read_at: string | null;
  unread_count: number;
  customer_requests: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string | null;
    wedding_date: string | null;
    wedding_location: string | null;
    budget: string | null;
    direct_request: boolean | null;
    created_at: string;
    intro_text: string | null;
    request_type: string | null;
    number_of_hours: string | null;
    special_requests: string | null;
    guest_details: any | null;
    total_suppliers_for_request: number | null;
    supplier_categories: {
      id: string;
      name: string;
    };
  } | null;
  supplier_requests: {
    id: string;
    accept: boolean | null;
    supplier_pages: {
      name: string;
      profile_image_url: string | null;
    } | null;
  } | null;
}

export const useSupplierUnansweredConversations = (supplierId?: string) => {
  return useQuery({
    queryKey: ["supplier-unanswered-conversations", supplierId],
    queryFn: async () => {
      if (!supplierId) return [];

      // Get all conversations where this supplier is involved
      const { data: conversations, error } = await supabase
        .from("conversations")
        .select(`
          id,
          customer_accept,
          confirm_booking,
          customer_deny,
          last_activity_at,
          customer_request_id,
          customer_last_read_at,
          supplier_last_read_at,
          customer_requests!inner (
            id,
            first_name,
            last_name,
            email,
            phone_number,
            wedding_date,
            wedding_location,
            budget,
            direct_request,
            created_at,
            intro_text,
            request_type,
            number_of_hours,
            special_requests,
            guest_details,
            total_suppliers_for_request,
            supplier_categories!inner (
              id,
              name
            )
          ),
          supplier_requests!inner (
            id,
            accept,
            supplier_pages!inner (
              name,
              profile_image_url,
              supplier_id
            )
          )
        `)
        .eq("supplier_requests.supplier_pages.supplier_id", supplierId)
        .eq("supplier_requests.accept", true) // Only accepted requests that became conversations
        .or("customer_deny.is.null,customer_deny.eq.false");

      if (error) {
        console.error("Error fetching supplier conversations:", error);
        throw error;
      }

      if (!conversations || conversations.length === 0) {
        return [];
      }

      // For each conversation, check if supplier has sent any messages
      const conversationsWithMessageCheck = await Promise.all(
        conversations.map(async (conversation) => {
          // Check if supplier has sent any messages in this conversation
          const { count, error: messageError } = await supabase
            .from("conversation_messages")
            .select("*", { count: "exact", head: true })
            .eq("conversation_id", conversation.id)
            .eq("sender_role", "supplier");

          if (messageError) {
            console.error("Error checking supplier messages:", messageError);
            return null;
          }

          const hasSupplierMessages = count !== null && count > 0;
          
          // Skip if supplier already sent messages
          if (hasSupplierMessages) {
            return null;
          }

          // Calculate unread count for customer messages since supplier last read
          const { count: unreadCount, error: unreadError } = await supabase
            .from("conversation_messages")
            .select("*", { count: "exact", head: true })
            .eq("conversation_id", conversation.id)
            .eq("sender_role", "customer")
            .gt("created_at", conversation.supplier_last_read_at || new Date(0).toISOString());

          const unread_count = unreadError ? 0 : (unreadCount || 0);

          return {
            ...conversation,
            unread_count: unread_count
          } as UnansweredConversation;
        })
      );

      // Filter out null values (conversations where supplier already sent messages)
      const unansweredConversations = conversationsWithMessageCheck.filter(
        (conv): conv is UnansweredConversation => conv !== null
      );

      return unansweredConversations;
    },
    enabled: !!supplierId,
  });
};